import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, ImportWizard } from './ui';
import {
  useC360List, C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon,
} from './c360';
import { usePageActions } from '../contexts/PermissionContext';

/**
 * Basit "tanım" (lookup) tablosu — iki alanlı (kod + açıklama/ad) CRUD ekranı.
 * c360 liste formatı (kolon filtreleri + Excel dışa aktarma), Kanban/Grafik YOK.
 *
 * Props (varsayılan: textId + description):
 *   apiBase, title, subtitle, newLabel, exportName
 *   codeField/codeOData/codeLabel/codeRequired   — 1. alan (varsayılan textId/TextId/Kod/zorunlu)
 *   descField/descOData/descriptionLabel/descRequired    — 2. alan (varsayılan description/Description/Açıklama/opsiyonel)
 *   entityLabel — silme/mesajlarda kullanılacak ("kayıt")
 */
export function NamedDefinitionPage({
  apiBase,
  title,
  listRoute,
  subtitle = 'Kayıtları filtreleyin, sıralayın ve yönetin.',
  newLabel,
  exportName = 'tanimlar',
  codeField = 'textId',
  codeOData = 'TextId',
  codeLabel = 'Kod',
  codeRequired = true,
  descField = 'description',
  descOData = 'Description',
  descriptionLabel = 'Açıklama',
  descRequired = false,
  entityLabel = 'kayıt',
}) {
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();
  const COLUMN_FILTER_META = useMemo(() => ({
    [codeField]: { type: 'text', odataField: codeOData },
    [descField]: { type: 'text', odataField: descOData },
  }), [codeField, codeOData, descField, descOData]);

  // Inline dizi her render yeni referans olur → hook fetchData'sı sonsuz döngüye girer; memoize et.
  const searchFields = useMemo(() => [codeOData, descOData], [codeOData, descOData]);

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [importOpen, setImportOpen] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const list = useC360List({
    apiBase,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields,
    defaultSortField: codeOData,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const openNew = () => navigate(`${listRoute}/new`);
  // #282 view=true → salt-okunur "izle"; edit → düzenlenebilir (?edit=1).
  const openDetail = (row = selected, view = false) => { if (!row) return; navigate(`${listRoute}/${row.id}${view ? '' : '?edit=1'}`, { state: { row, view } }); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${apiBase}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Kayıt başarıyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor(codeField, {
      header: codeLabel, meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor(descField, {
      header: descriptionLabel, meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('createdDate', {
      header: 'Oluşturma', enableColumnFilter: false,
      cell: (info) => { const v = info.getValue(); return v ? new Date(v).toLocaleDateString('tr-TR') : '-'; },
    }),
    columnHelper.accessor('updatedDate', {
      header: 'Güncelleme', enableColumnFilter: false,
      cell: (info) => { const v = info.getValue(); return v ? new Date(v).toLocaleDateString('tr-TR') : '-'; },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original, true); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openDetail(row.original, false); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [codeField, codeLabel, descField, descriptionLabel, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort(codeOData, 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: codeField, headerName: codeLabel },
        { field: descField, headerName: descriptionLabel },
        { headerName: 'Oluşturma', valueGetter: ({ row }) => (row.createdDate ? new Date(row.createdDate).toLocaleDateString('tr-TR') : '') },
        { headerName: 'Güncelleme', valueGetter: ({ row }) => (row.updatedDate ? new Date(row.updatedDate).toLocaleDateString('tr-TR') : '') },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `${exportName}_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  // === İçe aktarma ===
  const importPick = (row, keys) => {
    for (const k of keys) { if (row[k] != null && row[k] !== '') return String(row[k]).trim(); }
    return '';
  };
  const importRowToPayload = (row) => ({
    [codeField]: importPick(row, [codeLabel, codeField, 'Kod', 'Code', 'TextId']),
    [descField]: importPick(row, [descriptionLabel, descField, 'Açıklama', 'Aciklama', 'Ad', 'Description', 'Name']),
  });
  const importValidateRow = (p) => {
    if (codeRequired && !p[codeField]) return `${codeLabel} boş`;
    if (descRequired && !p[descField]) return `${descriptionLabel} boş`;
    return null;
  };
  const importSubmitRow = async (p) => {
    try {
      const res = await fetch(`${apiBase}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ [codeField]: p[codeField], [descField]: p[descField] }) });
      if (res.ok) return { ok: true };
      return { ok: false, error: `Sunucu ${res.status}` };
    } catch (e) {
      return { ok: false, error: e.message || 'Network hatası' };
    }
  };
  const downloadImportTemplate = () => {
    exportToExcel([{ [codeLabel]: '', [descriptionLabel]: '' }], null, `${exportName}_sablon`);
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder={`${codeLabel} veya ${descriptionLabel.toLowerCase()} ile filtrele...`}
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel/CSV'den İçe Aktar" aria-label="Excel/CSV'den İçe Aktar" onClick={() => setImportOpen(true)}><UploadIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title={title}
        subtitle={subtitle}
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ {newLabel || title}</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={(row) => openDetail(row, true)}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>"{selected?.[descField] || selected?.[codeField]}"</strong> {entityLabel}ını silmek istediğinize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { if (okCount > 0) { showToast('success', `${okCount} kayıt içe aktarıldı`); fetchData(); } }}
        title={`${title} İçe Aktar`}
        helpText={`Şablondaki kolon başlıklarını koruyun. Zorunlu alan: ${codeLabel}${descRequired ? ` ve ${descriptionLabel}` : ''}.`}
        rowToPayload={importRowToPayload}
        validateRow={importValidateRow}
        submitRow={importSubmitRow}
        downloadTemplate={downloadImportTemplate}
      />
      <Toast toast={toast} />
    </div>
  );
}
